/**
 * Agent System Utilities
 * Helper functions for agent/agency differentiation and operations
 */

import React from "react";
import { supabase } from "@/lib/supabase";

// Constants from database setup
export const AGENT_TAG_ID = 'a2785cc9-b2f6-4378-8331-36095aed1ab5';
export const FMA_BASIC_PLAN_ID = '8c1da3f3-5616-4179-9b9b-5878a7898b5d';

/**
 * Check if an organization is tagged as an agent
 */
export async function isAgentOrganization(organizationId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('entity_tags')
      .select('id')
      .eq('subject_id', organizationId)
      .eq('subject_type', 'organization')
      .eq('tag_id', AGENT_TAG_ID)
      .maybeSingle();

    if (error) {
      console.error('Error checking agent status:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error in isAgentOrganization:', error);
    return false;
  }
}

/**
 * Convert an agent organization to agency by removing agent tag
 */
export async function convertAgentToAgency(
  organizationId: string, 
  newAgencyName?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // 1. Update organization name if provided
    if (newAgencyName?.trim()) {
      const { error: nameError } = await supabase
        .from('organizations')
        .update({ name: newAgencyName.trim() })
        .eq('id', organizationId);

      if (nameError) {
        console.error('Failed to update organization name:', nameError);
        return { success: false, error: `Failed to update name: ${nameError.message}` };
      }
    }

    // 2. Remove agent tag (becomes agency automatically!)
    const { error: tagError } = await supabase
      .from('entity_tags')
      .delete()
      .eq('subject_id', organizationId)
      .eq('subject_type', 'organization')
      .eq('tag_id', AGENT_TAG_ID);

    if (tagError) {
      console.error('Failed to remove agent tag:', tagError);
      return { success: false, error: `Failed to convert: ${tagError.message}` };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error in convertAgentToAgency:', error);
    return { success: false, error: error.message || 'Unknown error occurred' };
  }
}

/**
 * Get organization type for display purposes
 */
export async function getOrganizationType(organizationId: string): Promise<'AGENT' | 'AGENCY'> {
  const isAgent = await isAgentOrganization(organizationId);
  return isAgent ? 'AGENT' : 'AGENCY';
}

/**
 * Get all agent organizations (for analytics/reporting)
 */
export async function getAllAgents() {
  try {
    const { data, error } = await supabase
      .from('organizations')
      .select(`
        id,
        name,
        createdat,
        entity_tags!inner(id)
      `)
      .eq('entity_tags.subject_type', 'organization')
      .eq('entity_tags.tag_id', AGENT_TAG_ID);

    if (error) {
      console.error('Error fetching agents:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllAgents:', error);
    return [];
  }
}

/**
 * Get all agency organizations (for analytics/reporting)
 */
export async function getAllAgencies() {
  try {
    // Get all orgs that DON'T have the agent tag
    const { data, error } = await supabase
      .from('organizations')
      .select(`
        id,
        name,
        createdat
      `)
      .not('id', 'in', 
        supabase
          .from('entity_tags')
          .select('subject_id')
          .eq('subject_type', 'organization')
          .eq('tag_id', AGENT_TAG_ID)
      );

    if (error) {
      console.error('Error fetching agencies:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getAllAgencies:', error);
    return [];
  }
}

/**
 * Agent signup analytics
 */
export async function getAgentSignupStats() {
  try {
    // Get total counts
    const { count: totalOrgs } = await supabase
      .from('organizations')
      .select('id', { count: 'exact', head: true });

    const { count: totalAgents } = await supabase
      .from('entity_tags')
      .select('id', { count: 'exact', head: true })
      .eq('subject_type', 'organization')
      .eq('tag_id', AGENT_TAG_ID);

    const totalAgencies = (totalOrgs || 0) - (totalAgents || 0);

    return {
      totalOrganizations: totalOrgs || 0,
      totalAgents: totalAgents || 0,
      totalAgencies,
      agentPercentage: totalOrgs ? Math.round((totalAgents || 0) / totalOrgs * 100) : 0
    };
  } catch (error) {
    console.error('Error in getAgentSignupStats:', error);
    return {
      totalOrganizations: 0,
      totalAgents: 0,
      totalAgencies: 0,
      agentPercentage: 0
    };
  }
}

/**
 * React hook for agent status with caching
 */
export function useAgentStatus(organizationId: string | null) {
  const [isAgent, setIsAgent] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!organizationId) {
      setIsAgent(null);
      setLoading(false);
      return;
    }

    let mounted = true;

    isAgentOrganization(organizationId)
      .then(result => {
        if (mounted) {
          setIsAgent(result);
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Error checking agent status:', error);
        if (mounted) {
          setIsAgent(false); // Default to agency on error
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [organizationId]);

  return { isAgent, loading };
}

// Export types for TypeScript
export type OrganizationType = 'AGENT' | 'AGENCY';

export interface AgentStats {
  totalOrganizations: number;
  totalAgents: number;
  totalAgencies: number;
  agentPercentage: number;
}